import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import { autopilotModelRunExecutionsEndpoint, autopilotModelRunImportEndpoint } from '../../../api/endpoints';
import { apiErrorCode } from '../../../utils/apiError';
import type { ModelRunExecution, ModelRunExecutionsResponse, ModelRunImportResponse } from '../../../types/autopilot';

// "Import a run": the nightly state machine's recent executions (pick one),
// or a run key typed by hand, imported into the model-runs tables and,
// when asked, promoted to the endpoint. The deployed API has no babel-node,
// so this is how staging and production promote while DL_ML_PROMOTE=0.

const importErrorMessage = (code: string | undefined, fallback: string) => {
    if (code === 'RunReferenceRequired') return 'Pick an execution or type a run key';
    if (code === 'InvalidExecutionArn') return 'That is not a Step Functions execution ARN';
    if (code === 'InvalidRunKey') return 'Run keys are letters, digits, dots, dashes and underscores';
    if (code === 'MlNotConfigured') return 'This API has no ML bucket configured';
    if (code === 'PromoteNotConfigured') return 'This API cannot promote: the image and SageMaker role are not configured';
    if (code === 'ImportFailed') return fallback || 'The import failed';
    return 'Could not import the run';
};

const submitLabel = (busy: boolean, promote: boolean) => {
    if (busy) return 'Working…';
    return promote ? 'Import and promote' : 'Import';
};

const when = (v: string | null) => (v ? dayjs(v).format('MMM D, HH:mm') : '—');

export function ImportRunDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: (runId: number | null) => Promise<unknown> | void }) {
    const [executions, setExecutions] = useState<ModelRunExecution[]>([]);
    const [canPromote, setCanPromote] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const [runKey, setRunKey] = useState('');
    const [promote, setPromote] = useState(false);
    const [autoServe, setAutoServe] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelected(null);
        setRunKey('');
        setPromote(false);
        setAutoServe(false);
        setLoading(true);
        axiosInstant
            .get<ModelRunExecutionsResponse>(autopilotModelRunExecutionsEndpoint(20))
            .then(({ data }) => {
                setExecutions(data.executions);
                setCanPromote(data.can_promote);
            })
            .catch((e) => {
                const detail = e && e.response && e.response.data ? e.response.data.message : '';
                toast.error(apiErrorCode(e) === 'ExecutionsUnavailable' ? `Could not list the training executions: ${detail}` : 'Could not list the training executions');
            })
            .finally(() => setLoading(false));
    }, [open]);

    const submit = async () => {
        setBusy(true);
        try {
            const body = selected ? { execution_arn: selected, promote, auto_serve: autoServe } : { run_key: runKey.trim(), promote, auto_serve: autoServe };
            const { data } = await axiosInstant.post<ModelRunImportResponse>(autopilotModelRunImportEndpoint(), body);
            if (data.promoted) toast.success(`Promoted: ${data.passed.length} target${data.passed.length === 1 ? '' : 's'} passed the gate`);
            else if (promote) toast.error('Imported, but no target passed the gate, so it was not promoted');
            else toast.success('Run imported');
            await onImported(data.run ? data.run.id : null);
            onClose();
        } catch (e: any) {
            const detail = e && e.response && e.response.data ? e.response.data.message : '';
            toast.error(importErrorMessage(apiErrorCode(e), detail));
        } finally {
            setBusy(false);
        }
    };

    const succeeded = executions.filter((x) => x.status === 'SUCCEEDED');
    const others = executions.filter((x) => x.status !== 'SUCCEEDED');
    const ready = !!selected || !!runKey.trim();

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Import a training run
                <IconButton aria-label="Close" onClick={onClose} size="small" disabled={busy}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <p className="text-[12.5px] text-text-2 mb-3">
                    A finished nightly execution becomes a run with its gate table. Promoting puts it on the endpoint; targets stay in shadow until their switch is on unless you serve every passing target now.
                </p>
                {loading && <div className="text-[12px] text-text-2 mb-2">Loading executions…</div>}
                {!loading && succeeded.length === 0 && <div className="text-[12px] text-text-2 mb-2">No finished executions to pick from. Type a run key below.</div>}
                <div className="max-h-64 overflow-y-auto border border-border-token rounded-lg mb-3">
                    {succeeded.map((x) => (
                        <label key={x.execution_arn} className={`flex items-center gap-2 px-3 py-2 border-b border-border-token last:border-b-0 cursor-pointer ${selected === x.execution_arn ? 'bg-tint/40' : ''}`}>
                            <input type="radio" name="execution" checked={selected === x.execution_arn} onChange={() => { setSelected(x.execution_arn); setRunKey(''); }} />
                            <span className="font-mono text-[12px] text-text-1 truncate">{x.name}</span>
                            <span className="text-[11.5px] text-text-2 ml-auto shrink-0">finished {when(x.stopped_at)}</span>
                            {x.imported_run_id !== null && <span className="text-[10.5px] font-bold uppercase border border-border-token rounded-md px-1.5 py-0.5 shrink-0">{x.imported_status || 'imported'}</span>}
                        </label>
                    ))}
                    {others.map((x) => (
                        <div key={x.execution_arn} className="flex items-center gap-2 px-3 py-2 border-b border-border-token last:border-b-0 opacity-60">
                            <span className="w-[13px]" />
                            <span className="font-mono text-[12px] truncate">{x.name}</span>
                            <span className="text-[11.5px] ml-auto shrink-0">{x.status.toLowerCase().replace('_', ' ')} · {when(x.started_at)}</span>
                        </div>
                    ))}
                </div>
                <TextField
                    size="small"
                    fullWidth
                    label="Or a run key"
                    placeholder="manual-2026-10-26-smoke"
                    value={runKey}
                    onChange={(e) => { setRunKey(e.target.value); if (e.target.value) setSelected(null); }}
                    helperText="The runs/<key>/summary folder in the ML bucket"
                />
                <div className="flex flex-col mt-2">
                    <FormControlLabel
                        control={<Checkbox size="small" checked={promote} disabled={!canPromote} onChange={(e) => { setPromote(e.target.checked); if (!e.target.checked) setAutoServe(false); }} />}
                        label={<span className="text-[13px]">Promote to the endpoint{canPromote ? '' : ' (not configured on this API)'}</span>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={autoServe} disabled={!promote} onChange={(e) => setAutoServe(e.target.checked)} />}
                        label={<span className="text-[13px]">Serve every target that passed the gate right away</span>}
                    />
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={submit} disabled={busy || !ready}>
                    {submitLabel(busy, promote)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
